import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Archive, ArchiveRestore, Ban, History, RotateCcw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { Organization } from '@nucleus-commerce/api-client/platform';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';

import { canManageOrganizations } from '@/shared/session/roles';
import { session } from '@/shared/session/session';
import { DetailList, DetailRow } from '@/shared/ui/detail-list';
import { Mono } from '@/shared/ui/mono';

import { EditOrganizationDialog } from '../components/edit-organization-dialog';
import { ExportCard } from '../components/export-card';
import { OrganizationActionDialog } from '../components/organization-action-dialog';
import { OrganizationStatusBadge } from '../components/organization-status-badge';
import { OwnersCard } from '../components/owners-card';
import { StartSupportSessionDialog } from '../components/start-support-session-dialog';
import { StoresCard } from '../components/stores-card';
import { SupportSessionsTable } from '../components/support-sessions-table';
import { UsageCard } from '../components/usage-card';
import { useOrganization } from '../hooks/use-organization';
import type { OrganizationAction } from '../hooks/use-organization-actions';

const ROUTE_ID = '/_authed/organizations/$organizationId' as const;

interface OrganizationActionsProps {
  organization: Organization;
  onAction: (action: OrganizationAction) => void;
}

/** The transitions available from the current status; only owners and admins see them. */
function OrganizationActions({ organization, onAction }: OrganizationActionsProps) {
  const { t } = useTranslation('organizations');
  const destructive = 'text-destructive hover:text-destructive';
  switch (organization.status) {
    case 'active':
      return (
        <>
          <StartSupportSessionDialog organization={organization} />
          <Button
            variant="outline"
            className={destructive}
            data-tour="platform.organizations.suspend"
            onClick={() => onAction('suspend')}
          >
            <Ban aria-hidden />
            {t('actions.suspend.label')}
          </Button>
          <Button variant="outline" className={destructive} onClick={() => onAction('close')}>
            <Archive aria-hidden />
            {t('actions.close.label')}
          </Button>
        </>
      );
    case 'suspended':
      return (
        <>
          <Button variant="outline" className={destructive} onClick={() => onAction('close')}>
            <Archive aria-hidden />
            {t('actions.close.label')}
          </Button>
          <Button
            data-tour="platform.organizations.reactivate"
            onClick={() => onAction('reactivate')}
          >
            <RotateCcw aria-hidden />
            {t('actions.reactivate.label')}
          </Button>
        </>
      );
    case 'closed':
      return (
        <>
          <Button variant="outline" className={destructive} onClick={() => onAction('delete')}>
            <Trash2 aria-hidden />
            {t('actions.delete.label')}
          </Button>
          <Button data-tour="platform.organizations.reopen" onClick={() => onAction('reopen')}>
            <ArchiveRestore aria-hidden />
            {t('actions.reopen.label')}
          </Button>
        </>
      );
  }
}

function OrganizationDetail() {
  const { t } = useTranslation('organizations');
  const { organizationId } = useParams({ from: ROUTE_ID });
  const navigate = useNavigate();
  const format = useFormat();
  const [action, setAction] = useState<OrganizationAction | null>(null);
  const { me } = session.useSession();
  const canManage = canManageOrganizations(me?.user.role);
  // Data is prefetched by the route loader, so this resolves from cache.
  const { data: organization } = useOrganization(organizationId);

  if (!organization) return null;

  return (
    <PageContent>
      <PageHeader
        title={<span className="font-serif text-[1.875rem] font-normal">{organization.name}</span>}
        meta={
          <span className="flex items-center gap-3">
            <Mono>{organization.slug}</Mono>
            <OrganizationStatusBadge status={organization.status} />
          </span>
        }
        description={
          <span title={format.utc(organization.createdAt)}>
            {t('detail.createdAt', { date: format.dateTime(organization.createdAt) })}
          </span>
        }
        backTo={{ to: '/organizations' }}
        backLabel={t('title')}
        actions={
          canManage ? (
            <span className="flex flex-wrap gap-2">
              <OrganizationActions organization={organization} onAction={setAction} />
            </span>
          ) : undefined
        }
      />
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2" data-tour="platform.organizations.profile">
          <CardHeader>
            <CardTitle>{t('detail.profile')}</CardTitle>
            <CardDescription>
              {t('detail.updatedAt', { date: format.dateTime(organization.updatedAt) })}
            </CardDescription>
            {canManage ? (
              <CardAction>
                <EditOrganizationDialog organization={organization} />
              </CardAction>
            ) : null}
          </CardHeader>
          <CardContent>
            <DetailList>
              <DetailRow label={t('detail.id')}>
                <Mono className="text-foreground">{organization.id}</Mono>
              </DetailRow>
              <DetailRow label={t('detail.slug')}>
                <Mono className="text-foreground">{organization.slug}</Mono>
              </DetailRow>
              <DetailRow label={t('detail.status')}>
                <OrganizationStatusBadge status={organization.status} />
              </DetailRow>
              {organization.status === 'suspended' && organization.suspendedReason ? (
                <DetailRow label={t('detail.suspendedReason')}>
                  {organization.suspendedReason}
                </DetailRow>
              ) : null}
              {organization.closedAt ? (
                <DetailRow label={t('detail.closedAt')}>
                  <span title={format.utc(organization.closedAt)}>
                    {format.dateTime(organization.closedAt)}
                  </span>
                </DetailRow>
              ) : null}
              <DetailRow label={t('detail.billingEmail')}>
                {organization.billingEmail ? (
                  <a href={`mailto:${organization.billingEmail}`} className="hover:underline">
                    {organization.billingEmail}
                  </a>
                ) : (
                  <span className="text-muted-foreground">{t('detail.noBillingEmail')}</span>
                )}
              </DetailRow>
              <DetailRow label={t('detail.plan')}>
                {organization.planId ? (
                  <Mono className="text-foreground">{organization.planId}</Mono>
                ) : (
                  <span className="text-muted-foreground">{t('detail.defaultPlan')}</span>
                )}
              </DetailRow>
              <DetailRow label={t('detail.staff')}>{organization.staffCount}</DetailRow>
              <DetailRow label={t('detail.created')}>
                <span title={format.utc(organization.createdAt)}>
                  {format.dateTime(organization.createdAt)}
                </span>
              </DetailRow>
              <DetailRow label={t('detail.notes')}>
                {organization.notes ? (
                  <span className="whitespace-pre-line">{organization.notes}</span>
                ) : (
                  <span className="text-muted-foreground">{t('detail.noNotes')}</span>
                )}
              </DetailRow>
            </DetailList>
          </CardContent>
        </Card>
        <OwnersCard
          organizationId={organization.id}
          owners={organization.owners}
          invitedOwners={organization.invitedOwners}
          staffCount={organization.staffCount}
          canManage={canManage}
        />
        <UsageCard organizationId={organization.id} />
        <ExportCard organizationId={organization.id} canManage={canManage} />
        <Card data-tour="platform.organizations.activity">
          <CardHeader>
            <CardTitle>{t('detail.activity.title')}</CardTitle>
            <CardDescription>{t('detail.activity.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline">
              <Link to="/audit-log" search={{ organizationId: organization.id }}>
                <History aria-hidden />
                {t('detail.activity.open')}
              </Link>
            </Button>
          </CardContent>
        </Card>
        <StoresCard className="lg:col-span-3" stores={organization.stores} />
        <Card
          className="gap-0 overflow-hidden py-0 lg:col-span-3"
          data-tour="platform.organizations.sessions"
        >
          <CardHeader className="border-b px-5 py-4">
            <CardTitle>{t('sessions.title')}</CardTitle>
            <CardDescription>{t('sessions.description')}</CardDescription>
          </CardHeader>
          <SupportSessionsTable
            filter={{ organizationId: organization.id }}
            canManage={canManage}
            label={t('sessions.title')}
          />
        </Card>
      </div>
      <OrganizationActionDialog
        organization={organization}
        action={action}
        onClose={() => setAction(null)}
        onDeleted={() => void navigate({ to: '/organizations' })}
      />
    </PageContent>
  );
}

export function OrganizationDetailPage() {
  return (
    <FeatureErrorBoundary name="organizations.detail">
      <OrganizationDetail />
    </FeatureErrorBoundary>
  );
}
